/**
 * Event date formatting, shared by the app and the microsite.
 *
 * Both surfaces show the same date in the same places now, so it is formatted
 * once. It lived only in the microsite while the app carried the date as a
 * literal string — which is how `14.11.2026` ended up hardcoded in a header, in
 * two translation files and in the privacy page, and had to be hunted down when
 * the date moved.
 *
 * Everything here builds the date from its parts and formats in UTC. Passing
 * `2026-11-28` to `new Date()` parses it as midnight UTC, and formatting that in
 * Europe/Athens is still the 28th — but the same trick with a time attached is
 * not safe, and the habit is what matters. It is also why nothing here uses
 * `new Date(string)` on a value that carries a time.
 */

const localeTag = (locale) => (locale === 'en' ? 'en-GB' : 'el-GR');

/** `2026-11-28` → `[2026, 11, 28]`, or null if it is not a date. */
function parts(value) {
  const [y, m, d] = String(value || '').split('-').map(Number);
  return y && m && d ? [y, m, d] : null;
}

/** «Σάββατο 28 Νοεμβρίου 2026» — the long form, for the hero and the microsite. */
export function formatEventDate(value, locale) {
  const p = parts(value);
  if (!p) return null;
  const [y, m, d] = p;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(localeTag(locale), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * «Σάββατο» / `Saturday` — the weekday alone, for the ticker.
 *
 * Derived rather than written down: the ticker names the day as well as the
 * date now, and a typed weekday is a second thing to get wrong when the
 * organisers move the event.
 */
export function formatEventWeekday(value, locale) {
  const p = parts(value);
  if (!p) return null;
  const [y, m, d] = p;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(localeTag(locale), {
    weekday: 'long',
    timeZone: 'UTC',
  });
}

/**
 * `28.11.2026` — the compact form used in the header, the ticker and beside the
 * speaker count. Deliberately not locale-formatted: it is a brand mark as much
 * as a date, and it reads the same in both languages.
 */
export function formatEventDateShort(value) {
  const p = parts(value);
  if (!p) return null;
  const [y, m, d] = p;
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
}

export default formatEventDate;
